import { useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

type Sizes = ReadonlyMap<string, { w: number; h: number }>;

/** Each card's size as drawn, by its `data-card-key`, measured again whenever one changes size or
 *  [cards] does. */
export function useCardSizes(surface: RefObject<HTMLElement | null>, cards: unknown): Sizes {
  const [sizes, setSizes] = useState<Sizes>(() => new Map());

  useLayoutEffect(() => {
    const root = surface.current;
    if (!root) return;
    const measure = () => {
      const next = new Map<string, { w: number; h: number }>();
      root.querySelectorAll<HTMLElement>('[data-card-key]').forEach((el) => {
        next.set(el.dataset['cardKey'] ?? '', { w: el.offsetWidth, h: el.offsetHeight });
      });
      setSizes((prev) => (sameSizes(prev, next) ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    root.querySelectorAll('[data-card-key]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [surface, cards]);

  return sizes;
}

function sameSizes(a: Sizes, b: Sizes): boolean {
  if (a.size !== b.size) return false;
  for (const [key, s] of b) {
    const t = a.get(key);
    if (!t || t.w !== s.w || t.h !== s.h) return false;
  }
  return true;
}
